import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { useRunReportGuard } from '@/pages/RunReport/guards/useRunReportGuard'
import { useRunReportWizardStep } from '@/pages/RunReport/hooks/useRunReportWizardStep'
import { EReportType, ERunReportStep } from '@/pages/RunReport/types'
import { selectRunReport } from '@/store/api/RunReport/RunReport.selector'
import {
  setSocialMedia,
  setSubjectInfo,
} from '@/store/api/RunReport/RunReport.slice'
import type { addExtraDetailsSchemaType } from '@/utils/schemas'
import { addExtraDetailsSchema } from '@/utils/schemas'

import { ProfilePictureField } from '../../components/ProfilePictureField'
import { RunReportStepIndicator } from '../../components/RunReportStepIndicator'
import { SocialMediaAccountsField } from '../../components/SocialMediaAccountsField'

export const AddExtraDetails = () => {
  useRunReportGuard(ERunReportStep.AddExtraDetails)
  const { current, total } = useRunReportWizardStep(
    ERunReportStep.AddExtraDetails,
  )
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const slice = useAppSelector(selectRunReport)

  const isAdverseMedia =
    slice.selectSearchType.reportType === EReportType.AdverseMediaReport

  const form = useForm<addExtraDetailsSchemaType>({
    resolver: zodResolver(addExtraDetailsSchema),
    defaultValues: {
      image:
        typeof slice.subjectInfo.image === 'string'
          ? slice.subjectInfo.image
          : null,
      employerName: slice.subjectInfo.employerName,
      college: slice.subjectInfo.college,
      alias: slice.subjectInfo.alias,
      phoneNumber: slice.subjectInfo.phoneNumber,
      description: slice.subjectInfo.description,
      socialMedia: slice.socialMedia,
    },
    mode: 'onChange',
  })

  const { handleSubmit, setValue, control, trigger, watch } = form

  const watchedValues = watch()
  const isFormValid = addExtraDetailsSchema.safeParse(watchedValues).success

  useEffect(() => {
    const formFields: Partial<
      Record<keyof addExtraDetailsSchemaType, unknown>
    > = {
      image:
        typeof slice.subjectInfo.image === 'string'
          ? slice.subjectInfo.image
          : null,
      employerName: slice.subjectInfo.employerName,
      college: slice.subjectInfo.college,
      alias: slice.subjectInfo.alias,
      phoneNumber: slice.subjectInfo.phoneNumber,
      description: slice.subjectInfo.description,
      socialMedia: slice.socialMedia,
    }
    const hydratedKeys: (keyof addExtraDetailsSchemaType)[] = []
    Object.entries(formFields).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        const typedKey = key as keyof addExtraDetailsSchemaType
        setValue(typedKey, value as never, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
        hydratedKeys.push(typedKey)
      }
    })
    if (hydratedKeys.length > 0) trigger(hydratedKeys)
  }, [slice.subjectInfo, slice.socialMedia, setValue, trigger])

  const onSubmit = (data: addExtraDetailsSchemaType) => {
    dispatch(
      setSubjectInfo({
        ...slice.subjectInfo,
        image: data.image,
        employerName: data.employerName ?? '',
        college: data.college ?? '',
        alias: data.alias ?? '',
        phoneNumber: data.phoneNumber ?? '',
        description: data.description ?? '',
      }),
    )
    dispatch(setSocialMedia(data.socialMedia))
    navigate(AppRoutes.runReportBehaviorFlags)
  }

  const handleSkip = () => {
    navigate(AppRoutes.runReportBehaviorFlags)
  }

  const handleBack = () => {
    if (
      slice.selectSearchType.reportType === EReportType.AiReport &&
      !slice.isPrefillForm
    ) {
      navigate(AppRoutes.runReportRefineSearch)
      return
    }
    navigate(AppRoutes.runReportSubjectForm)
  }

  return (
    <WeScreen
      useNextButton={false}
      useSkipButton={false}
      onClickBack={handleBack}
      hideHeaderOnMobile
      contentClassName="p-0"
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex h-[calc(100dvh-3.5rem)] min-h-0 flex-col lg:h-full lg:flex-row lg:gap-6 lg:overflow-hidden lg:p-8"
        >
          <div className="hidden h-full flex-col lg:flex lg:flex-1">
            <div className="flex flex-1 flex-col justify-center gap-3 px-3">
              <RunReportStepIndicator current={current} total={total} />
              <h2 className="text-[32px] font-medium leading-tight text-neutralColor">
                {t('dashboard.runReport.addExtraDetails.title')}
              </h2>
              <p className="text-base text-neutralColor">
                {t('dashboard.runReport.addExtraDetails.subtitle')}
              </p>

              <div className="mt-2 flex gap-4">
                <Button type="button" variant="secondary" onClick={handleSkip}>
                  {t('dashboard.runReport.addExtraDetails.skip')}
                </Button>
                <Button type="submit" disabled={!isFormValid}>
                  {t('dashboard.runReport.addExtraDetails.confirm')}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-6 lg:h-full lg:overflow-hidden lg:p-0">
            <div className="flex min-h-0 flex-1 flex-col gap-5 rounded-2xl bg-baseColorHighlight p-4 lg:h-full lg:min-h-0 lg:gap-6 lg:overflow-y-auto lg:p-6">
              <div className="flex flex-col gap-4 lg:flex-1">
                <div className="flex flex-col gap-2">
                  <p className="text-base font-medium text-neutralColor lg:text-xl">
                    {t('dashboard.runReport.addExtraDetails.descriptionTitle')}
                  </p>
                  <p className="text-sm text-neutralColor60">
                    {t('dashboard.runReport.addExtraDetails.descriptionHint')}
                  </p>
                  <FormField
                    control={control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="sr-only">
                          {t('dashboard.runReport.addExtraDetails.description')}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={field.value ?? ''}
                            maxLength={2000}
                            placeholder={t(
                              'dashboard.runReport.addExtraDetails.descriptionPlaceholder',
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="border-t border-neutralColor5" />

                <p className="text-base font-medium text-neutralColor lg:text-xl">
                  {t(
                    'dashboard.runReport.addExtraDetails.additionalInformationTitle',
                  )}
                </p>
                <div className="flex flex-col gap-3 md:flex-row md:gap-6">
                  <FormField
                    control={control}
                    name="employerName"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel className="sr-only">
                          {t('dashboard.runReport.addExtraDetails.employer')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={t(
                              'dashboard.runReport.addExtraDetails.employerPlaceholder',
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="college"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel className="sr-only">
                          {t('dashboard.runReport.addExtraDetails.school')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={t(
                              'dashboard.runReport.addExtraDetails.schoolPlaceholder',
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={control}
                  name="alias"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="sr-only">
                        {t(
                          'dashboard.runReport.addExtraDetails.subjectUsername',
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t(
                            'dashboard.runReport.addExtraDetails.subjectUsernamePlaceholder',
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="sr-only">
                        {t('dashboard.runReport.addExtraDetails.phoneNumber')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="tel"
                          placeholder={t(
                            'dashboard.runReport.addExtraDetails.phoneNumberPlaceholder',
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="border-t border-neutralColor5" />

              <ProfilePictureField
                hint={t(
                  'dashboard.runReport.addExtraDetails.profilePictureHint',
                )}
              />

              {!isAdverseMedia && (
                <>
                  <div className="border-t border-neutralColor5" />
                  <SocialMediaAccountsField />
                </>
              )}
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-3 bg-baseColor px-4 pb-4 pt-3 lg:hidden">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-medium leading-tight text-neutralColor">
                {t('dashboard.runReport.addExtraDetails.title')}
              </h2>
              <p className="text-xs text-neutralColor60">
                {t('dashboard.runReport.addExtraDetails.subtitle')}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={handleSkip}
                className="flex-1"
              >
                {t('dashboard.runReport.addExtraDetails.skip')}
              </Button>
              <Button type="submit" disabled={!isFormValid} className="flex-1">
                {t('dashboard.runReport.addExtraDetails.confirm')}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </WeScreen>
  )
}
